/**
 * 侧边栏主导航组件
 * 
 * 职责：
 * 1. 展示主要导航菜单（未分类、全部收藏等）
 * 2. 支持多级菜单展开/收起
 * 3. 处理菜单项的激活状态
 */

import { ChevronRight } from "lucide-react";
import { Link } from "react-router";

import { useFolderMoveDropTarget } from "@/modules/mine";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarMenuBadge,
  useSidebar,
} from "@/shared/ui/sidebar";

import type { MainNavItem, RouteInfo } from "./types";

/**
 * 组件 Props 接口
 */
interface SidebarPrimaryProps {
  items: MainNavItem[];
  routeInfo: RouteInfo;
}

/**
 * 侧边栏主导航组件
 */
function SidebarPrimary({ items, routeInfo }: SidebarPrimaryProps) {
  const { isMobile } = useSidebar();
  const {
    ref: uncategorizedDropRef,
    isDropTarget: isUncategorizedDropTarget,
    isDropAvailable: isUncategorizedDropAvailable,
  } = useFolderMoveDropTarget({
    folderId: "0",
    folderName: "未分类",
    disabled: isMobile,
  });

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          {items.map((item) => {
            const isUncategorized = item.url === ROUTE_PATHS.MINE;

            return (
              <Collapsible
                key={item.title}
                asChild
                defaultOpen={routeInfo.pathname === "/mine/all" && !!routeInfo.format}
              >
                <SidebarMenuItem
                  ref={isUncategorized ? uncategorizedDropRef : undefined}
                >
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    className={
                      isUncategorized && isUncategorizedDropTarget
                        ? "bg-primary/10 text-primary ring-2 ring-inset ring-primary/70"
                        : isUncategorized && isUncategorizedDropAvailable
                          ? "relative after:pointer-events-none after:absolute after:inset-0 after:animate-pulse after:rounded-md after:border-2 after:border-dashed after:border-primary/30 motion-reduce:after:animate-none"
                          : undefined
                    }
                    isActive={
                      item.items
                        ? item.url === routeInfo.pathname && !routeInfo.format
                        : item.url === routeInfo.pathname
                    }
                  >
                    <Link to={item.url}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                  {item.badge && (
                    <SidebarMenuBadge> {item.badge} </SidebarMenuBadge>
                  )}
                  {item.subIcon && (
                    <SidebarMenuBadge>
                      {<item.subIcon className="ml-auto size-4 text-gray-500" />}
                    </SidebarMenuBadge>
                  )}
                  {item.items?.length ? (
                    <>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction className="data-[state=open]:rotate-90">
                          <ChevronRight />
                          <span className="sr-only">Toggle</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.items.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={
                                  subItem.url ===
                                  routeInfo.pathname + routeInfo.search
                                }
                              >
                                <Link to={subItem.url}>
                                  {subItem.icon && <subItem.icon />}
                                  <span>{subItem.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </>
                  ) : null}
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export default SidebarPrimary;
